import type { LoomInspectionSurfaceKind } from "./LoomSurfaceAppearanceControl";
import {
  LOOM_DEFAULT_VIEWPORT_PRESENTATION,
  type LoomAssemblyView,
  type LoomSurfaceStyle,
  type LoomViewportPresentationState,
} from "./loomViewportPresentation";

interface LoomViewportPresentationControlProps {
  onCameraFitRequest: () => void;
  onChange: (state: LoomViewportPresentationState) => void;
  state: LoomViewportPresentationState;
  surfaceKind: LoomInspectionSurfaceKind;
}

const SURFACE_STYLES: readonly { id: LoomSurfaceStyle; label: string }[] = [
  { id: "filled", label: "Filled" },
  { id: "filled-wire", label: "Filled + wire" },
  { id: "wireframe", label: "Wireframe" },
];
const ASSEMBLY_VIEWS: readonly { id: LoomAssemblyView; label: string }[] = [
  { id: "assembled", label: "Assembled" },
  { id: "exploded", label: "Exploded" },
  { id: "highlight-only", label: "Highlight only" },
];

export function LoomViewportPresentationControl({
  onCameraFitRequest,
  onChange,
  state,
  surfaceKind,
}: LoomViewportPresentationControlProps) {
  const change = (patch: Partial<LoomViewportPresentationState>) => onChange({ ...state, ...patch });
  const sourceCopy = surfaceKind === "authored-operands"
    ? "Presentation only · visible authored operands"
    : surfaceKind === "gpu-implicit-union"
      ? "Presentation only · GPU union when assembled and filled"
      : "Presentation only · retained explicit faces";
  return (
    <div className="loom-viewport-presentation-control" data-testid="loom-viewport-presentation-controls">
      <div aria-label="Surface style" className="loom-presentation-segment" role="group">
        {SURFACE_STYLES.map(({ id, label }) => (
          <button
            aria-pressed={state.surfaceStyle === id}
            data-testid={`loom-surface-style-${id}`}
            key={id}
            onClick={() => change({ surfaceStyle: id })}
            type="button"
          >{label}</button>
        ))}
      </div>
      <div aria-label="Assembly view" className="loom-presentation-segment" role="group">
        {ASSEMBLY_VIEWS.map(({ id, label }) => {
          return <button
            aria-pressed={state.assemblyView === id}
            data-testid={`loom-assembly-view-${id}`}
            key={id}
            onClick={() => change({ assemblyView: id })}
            type="button"
          >{label}</button>;
        })}
      </div>
      <label className="loom-explode-amount">
        <span>Explode <output>{Math.round(state.explodeAmount * 100)}%</output></span>
        <input
          aria-label="Explode amount"
          data-testid="loom-explode-amount"
          disabled={state.assemblyView !== "exploded"}
          max={1}
          min={0.1}
          onChange={(event) => change({ explodeAmount: Number(event.target.value) })}
          step={0.05}
          type="range"
          value={state.explodeAmount}
        />
      </label>
      <button
        className="loom-presentation-reset"
        data-testid="loom-reset-presentation"
        aria-label="Reset presentation and fit camera"
        onClick={() => {
          onChange(LOOM_DEFAULT_VIEWPORT_PRESENTATION);
          onCameraFitRequest();
        }}
        title="Restore the default presentation and fit all visible geometry"
        type="button"
      >Reset &amp; fit view</button>
      <span className="loom-presentation-source">{sourceCopy}</span>
    </div>
  );
}
